import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { inr, inrShort } from "@/lib/api";
import { TrendingUp, Home, Layers, IndianRupee, Share2, ImageDown, ShieldCheck } from "lucide-react";

const WhatsappIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const StatCard = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <Card
    className="bg-slate-900/70 border-slate-800 rounded-2xl p-5 hover:-translate-y-1 hover:border-orange-500/40 transition-all"
    data-testid={testId}
  >
    <div className={`h-9 w-9 rounded-lg grid place-items-center mb-3 ${accent}`}>
      <Icon className="h-4.5 w-4.5" />
    </div>
    <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">{label}</p>
    <p className="font-heading text-2xl font-extrabold text-white mt-1 font-mono-data">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
  </Card>
);

export const ResultsDashboard = ({ result, onShare, onWhatsApp, onSaveImage, pmay }) => {
  if (!result) return null;
  const pieData = result.breakdown.map((b) => ({ name: b.label, value: b.amount, color: b.color }));

  return (
    <div className="space-y-6" data-testid="results-dashboard">
      {/* Hero total */}
      <motion.div
        key={result.total_cost}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Card className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-amber-600 border-0 rounded-2xl p-7">
          <div className="absolute inset-0 grain-overlay opacity-20" />
          <div className="absolute top-5 right-5 z-10 flex gap-2">
            <button
              onClick={onWhatsApp}
              data-testid="btn-whatsapp-share"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] hover:bg-[#1ebe5b] px-3 py-1.5 text-xs font-semibold text-white transition-colors"
            >
              <WhatsappIcon /> WhatsApp
            </button>
            <button
              onClick={onSaveImage}
              data-testid="btn-save-image"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white transition-colors"
            >
              <ImageDown className="h-3.5 w-3.5" /> Image
            </button>
            <button
              onClick={onShare}
              data-testid="btn-share-estimate"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
          <div className="relative">
            <p className="text-sm font-semibold text-orange-50/90 uppercase tracking-wider">
              Estimated Construction Cost
            </p>
            <div className="flex items-end gap-3 mt-2 flex-wrap">
              <h2
                className="font-heading text-4xl sm:text-5xl font-extrabold text-white font-mono-data"
                data-testid="summary-total-cost"
              >
                ₹{inr(result.total_cost)}
              </h2>
              <span className="text-lg font-semibold text-orange-100 mb-1.5">
                ({inrShort(result.total_cost)})
              </span>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-orange-50">
              <span>
                <b className="font-mono-data">₹{inr(result.cost_per_sqft)}</b> / sq.ft
              </span>
              <span>
                Built-up: <b className="font-mono-data">{inr(result.inputs.total_area)}</b> sq.ft
              </span>
              <span>{result.inputs.city} · {result.inputs.quality}</span>
            </div>
          </div>
        </Card>
      </motion.div>

      {pmay && pmay.eligible && (
        <div
          data-testid="net-cost-banner"
          className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-center justify-between gap-4 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/20 grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm text-emerald-300 font-semibold">
                After PMAY subsidy (−₹{inr(pmay.estimated_subsidy)})
              </p>
              <p className="text-xs text-slate-400">
                Category {pmay.category_id ? pmay.category_id.toUpperCase() : ""} ·{" "}
                {pmay.subsidy_rate}% interest subsidy
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Net Cost</p>
            <p
              className="font-heading text-2xl font-extrabold text-white font-mono-data"
              data-testid="net-cost-value"
            >
              ₹{inr(result.total_cost - pmay.estimated_subsidy)}
            </p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Home}
          label="Total Area"
          value={`${inr(result.inputs.total_area)}`}
          sub="sq.ft built-up"
          testId="stat-area"
          accent="bg-blue-500/15 text-blue-400"
        />
        <StatCard
          icon={Layers}
          label="Floors"
          value={result.inputs.floors}
          sub={result.inputs.foundation}
          testId="stat-floors"
          accent="bg-emerald-500/15 text-emerald-400"
        />
        <StatCard
          icon={IndianRupee}
          label="Base Rate"
          value={`₹${inr(result.effective_rate)}`}
          sub="per sq.ft"
          testId="stat-rate"
          accent="bg-orange-500/15 text-orange-400"
        />
        <StatCard
          icon={TrendingUp}
          label="Add-ons"
          value={inrShort(result.addon_total)}
          sub={`${result.addons.length} extras`}
          testId="stat-addons"
          accent="bg-pink-500/15 text-pink-400"
        />
      </div>

      {/* Breakdown */}
      <Card className="bg-slate-900/70 border-slate-800 rounded-2xl p-6" data-testid="cost-breakdown">
        <h3 className="font-heading text-lg font-bold text-white mb-4">Cost Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={95}
                  paddingAngle={2}
                  stroke="none"
                >
                  {pieData.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => `₹${inr(v)}`}
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2.5">
            {result.breakdown.map((b) => (
              <div key={b.key} className="flex items-center gap-3" data-testid={`breakdown-${b.key}`}>
                <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: b.color }} />
                <span className="text-sm text-slate-300 flex-1">{b.label}</span>
                <span className="text-xs text-slate-500 font-mono-data w-12 text-right">{b.pct}%</span>
                <span className="text-sm font-semibold text-white font-mono-data w-24 text-right">
                  ₹{inr(b.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
